// versions-chrome — student-versions (Trello ch3IfCAK): "Save version" and "Versions" on the
// student's own assignment working copy.
//
// The THIRTEENTH sibling chrome module. Plain DOM, no Lit. It ALLOW-LISTS
// `body[data-surface="assignment"]` and is a no-op anywhere else and on a page with no
// `#versions-config` (the server emits that only on the student's own working copy, with the
// teacher's "Let students save versions" switch on, and never on a plan-locked page). Wired LATE
// from main.js, after `wireTestTable` and `wireVariableTrace`: it needs `core` (the live files),
// and its capture-phase Submit nudge must sit behind the test table's own Submit guard.
//
// SAVE (decisions.md Round 3, final order):
//   1. await `flushPendingSave()` (assignment-chrome's debounce, flushed now);
//   2. take `core.getFiles()` and pass the `main.py` entry through `insertChangesLine`;
//   3. await `saveDraft(edited)` and REQUIRE `success: true` (`project.draft` swallows errors
//      into a 200);
//   4. POST the version — the SERVER reads the code it just saved (never a posted string);
//   5. on success, leave a one-shot sessionStorage message and `location.reload()`. On a failed
//      POST the ORIGINAL files are saved back, an inline error shows and nothing reloads.
// The live buffer is never edited (no Ace insert, no `core.load`): an insert trips the
// authenticity large-insert detector and `load` wipes staged assets. The reload shows the line.
//
// RESTORE is server-side: the server saves the current code as an automatic version, writes
// the chosen version into the Draft, and the page reloads.
//
// While a request runs the editor is read-only (through `core.getEditor()`) and the dialog's
// buttons are disabled; a failure gives the editor back. Never touches editor-core / editor-api
// internals and adds nothing to the runner contract.

import { insertChangesLine } from './versions-changes-line.js';
import { readScheduleLock, disableForLock, lockSentence } from './schedule-lock.js';

var NAME_MAX = 40;
var NOTE_MAX = 300;
var FROM_MAX = 30;
var PREVIEW_REASON = 'Not saved in preview — you can look at versions here, but nothing is saved or restored.';
var LOCK_REASON = 'This activity is locked, so you can look at your versions but not go back to one.';

function readConfig(doc) {
  var el = doc.getElementById('versions-config');
  if (!el) { return null; }
  try {
    var cfg = JSON.parse(el.textContent || '');
    return (cfg && typeof cfg === 'object' && cfg.materialId && cfg.projectId) ? cfg : null;
  } catch (e) { return null; }
}

function sessionStore() {
  try { return window.sessionStorage || null; } catch (e) { return null; }
}

export function messageKey(cfg) {
  return 'pcv-msg:' + String(cfg.materialId || '') + ':' + String(cfg.projectId || '');
}

// Read AND remove the one-shot post-reload message. Storage can be missing or throw — that
// reads as "no message" and the page renders normally.
export function takeMessage(storage, key) {
  try {
    if (!storage) { return ''; }
    var v = storage.getItem(key);
    if (v !== null) { storage.removeItem(key); }
    return v || '';
  } catch (e) { return ''; }
}

function putMessage(key, text) {
  try { var s = sessionStore(); if (s) { s.setItem(key, text); } } catch (e) { /* the reload still happens */ }
}

export function clip(text, max) {
  var s = String(text == null ? '' : text);
  return s.length > max ? s.slice(0, max - 1).replace(/\s+$/, '') + '…' : s;
}

// "3 minutes ago" via Intl.RelativeTimeFormat (computed when the list opens; not refreshed).
export function relativeTime(iso, now) {
  var t = new Date(iso).getTime();
  if (isNaN(t)) { return ''; }
  var diff = Math.round((t - (now || Date.now())) / 1000);
  var abs = Math.abs(diff);
  var units = [['second', 60], ['minute', 60], ['hour', 24], ['day', 7], ['week', 4.35], ['month', 12], ['year', Infinity]];
  var value = diff, unit = 'second';
  for (var i = 0; i < units.length; i++) {
    unit = units[i][0];
    if (abs < units[i][1]) { break; }
    value = value / units[i][1];
    abs = abs / units[i][1];
  }
  try {
    var rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
    return rtf.format(Math.round(value), unit);
  } catch (e) {
    return new Date(t).toLocaleString();
  }
}

// Has THIS activity a version (automatic ones count; a chain ancestor's do not)?
export function hasOwnVersion(versions, materialId) {
  return (versions || []).some(function (v) { return v && v.materialId === materialId && !v.fromMaterialId; });
}

function errorText(body, fallback) {
  return (body && typeof body.message === 'string' && body.message) ? body.message : fallback;
}

function requestJson(url, method, payload) {
  var opts = { method: method, credentials: 'same-origin', headers: { Accept: 'application/json' } };
  if (payload) {
    opts.headers['Content-Type'] = 'application/json';
    opts.body = JSON.stringify(payload);
  }
  return fetch(url, opts).then(function (res) {
    return res.json().catch(function () { return {}; }).then(function (body) {
      return { ok: res.ok, status: res.status, body: body || {} };
    });
  });
}

// The focusable controls inside a dialog, in order, skipping hidden / disabled ones.
function focusables(root) {
  var all = root.querySelectorAll('button, input, textarea, [href], [tabindex]:not([tabindex="-1"])');
  var out = [];
  for (var i = 0; i < all.length; i++) {
    var el = all[i];
    if (el.disabled || el.hidden || el.closest('[hidden]')) { continue; }
    out.push(el);
  }
  return out;
}

export function wireVersions(opts) {
  var o = opts || {};
  var doc = o.doc;
  var core = o.core;
  var api = o.editorApi;
  if (!doc || !doc.body || doc.body.getAttribute('data-surface') !== 'assignment') { return null; }
  var cfg = readConfig(doc);
  if (!cfg || !core || !api) { return null; }

  var flush = typeof o.flushPendingSave === 'function' ? o.flushPendingSave : function () { return Promise.resolve({ success: true }); };
  var body = doc.body;
  var saveBtn = doc.getElementById('versions-save');
  var listBtn = doc.getElementById('versions-list');
  var submitBtn = doc.getElementById('assignment-submit');
  var live = doc.getElementById('versions-live');
  if (!saveBtn || !listBtn) { return null; }

  var preview = cfg.previewMode === true || body.getAttribute('data-preview') === 'true';
  var lock = readScheduleLock(doc);
  var readOnlyReason = preview ? PREVIEW_REASON : (lock.enforce ? LOCK_REASON : '');

  var base = '/api/courses/' + encodeURIComponent(cfg.courseId) + '/lessons/' + encodeURIComponent(cfg.lessonId)
    + '/materials/' + encodeURIComponent(cfg.materialId) + '/versions';
  var state = { versions: [], nextName: 'v1', loaded: false, failed: false };
  var busy = false;
  var bypassNudge = false;
  var opener = null;
  var openModal = null;

  function announce(text) {
    if (!live) { return; }
    live.textContent = '';
    setTimeout(function () { live.textContent = text; }, 30);
  }

  function editorReadOnly() {
    return !!(core.isReadOnly && core.isReadOnly());
  }
  function setEditorLocked(on) {
    var ed = core.getEditor && core.getEditor();
    if (ed && ed.setReadOnly) { ed.setReadOnly(on ? true : editorReadOnly()); }
  }

  // ── the buttons ──────────────────────────────────────────────────────────────────────────
  listBtn.disabled = false;
  if (preview) {
    saveBtn.disabled = true;
    saveBtn.title = 'Not saved in preview';
  } else if (lock.enforce) {
    disableForLock(doc, saveBtn);
  } else if (editorReadOnly()) {
    saveBtn.disabled = true;
  } else {
    saveBtn.disabled = false;
  }

  // ── the list read ────────────────────────────────────────────────────────────────────────
  function loadList() {
    var q = '?projectId=' + encodeURIComponent(cfg.projectId) + '&linkedCourseId=' + encodeURIComponent(cfg.linkedCourseId || '');
    return requestJson(base + q, 'GET').then(function (r) {
      if (!r.ok || !Array.isArray(r.body.versions)) { throw new Error('list ' + r.status); }
      state.versions = r.body.versions;
      state.nextName = typeof r.body.nextName === 'string' ? r.body.nextName : 'v1';
      state.loaded = true;
      state.failed = false;
      return state;
    }).catch(function (err) {
      state.failed = true;
      throw err;
    });
  }
  var firstLoad = loadList().catch(function () { return null; });

  // ── dialogs: open / close / focus trap / Escape ──────────────────────────────────────────
  function showModal(id, from, focusEl) {
    var modal = doc.getElementById(id);
    if (!modal) { return null; }
    if (openModal && openModal !== modal) { openModal.hidden = true; }
    opener = from || opener;
    modal.hidden = false;
    openModal = modal;
    var target = focusEl || focusables(modal)[0];
    if (target) { target.focus(); }
    return modal;
  }
  function closeModal(returnTo) {
    if (busy || !openModal) { return; }
    openModal.hidden = true;
    openModal = null;
    var back = returnTo || opener;
    opener = null;
    if (back && back.focus) { back.focus(); }
  }
  doc.addEventListener('keydown', function (e) {
    if (!openModal) { return; }
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      closeModal();
      return;
    }
    if (e.key !== 'Tab') { return; }
    var list = focusables(openModal);
    if (!list.length) { e.preventDefault(); return; }
    var first = list[0], last = list[list.length - 1];
    if (e.shiftKey && doc.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && doc.activeElement === last) { e.preventDefault(); first.focus(); }
    else if (list.indexOf(doc.activeElement) === -1) { e.preventDefault(); first.focus(); }
  }, true);
  ['versions-save-modal', 'versions-list-modal', 'versions-nudge-modal'].forEach(function (id) {
    var modal = doc.getElementById(id);
    if (!modal) { return; }
    modal.addEventListener('click', function (e) { if (e.target === modal) { closeModal(); } });
  });

  // ── Save version ─────────────────────────────────────────────────────────────────────────
  var nameEl = doc.getElementById('versions-save-name');
  var noteEl = doc.getElementById('versions-save-note');
  var countEl = doc.getElementById('versions-save-count');
  var saveStatus = doc.getElementById('versions-save-status');
  var saveConfirm = doc.getElementById('versions-save-confirm');
  var saveCancel = doc.getElementById('versions-save-cancel');

  function setSaveStatus(text, isError) {
    if (!saveStatus) { return; }
    saveStatus.textContent = text || '';
    saveStatus.classList.toggle('runner-error', !!isError);
  }
  function updateCount() {
    if (countEl && noteEl) { countEl.textContent = noteEl.value.length + ' / ' + NOTE_MAX; }
  }
  if (noteEl) { noteEl.addEventListener('input', updateCount); }

  function openSave(from) {
    if (saveBtn.disabled || preview || lock.enforce || editorReadOnly()) { return; }
    if (nameEl) { nameEl.value = state.nextName || 'v1'; }
    if (noteEl) { noteEl.value = ''; }
    updateCount();
    setSaveStatus('', false);
    showModal('versions-save-modal', from || saveBtn, nameEl);
    if (nameEl && nameEl.select) { nameEl.select(); }
  }

  function setSaveBusy(on) {
    busy = on;
    [saveConfirm, saveCancel, nameEl, noteEl].forEach(function (el) { if (el) { el.disabled = on; } });
    if (saveConfirm) { saveConfirm.textContent = on ? 'Saving…' : 'Save version'; }
    if (saveConfirm) { saveConfirm.setAttribute('aria-busy', on ? 'true' : 'false'); }
  }

  function editedFiles(files, name, note) {
    var touched = false;
    var out = (files || []).map(function (f) {
      if (!touched && f && f.name === 'main.py') {
        touched = true;
        return Object.assign({}, f, { content: insertChangesLine(f.content || '', name, note) });
      }
      return f;
    });
    return out;
  }

  function doSave() {
    if (busy) { return; }
    var name = nameEl ? nameEl.value.trim() : '';
    var note = noteEl ? noteEl.value.trim() : '';
    if (!name) { setSaveStatus('Give this version a name.', true); if (nameEl) { nameEl.focus(); } return; }
    if (name.length > NAME_MAX) { setSaveStatus('Keep the name to 40 characters or fewer.', true); return; }
    if (!note) { setSaveStatus('Say what changed.', true); if (noteEl) { noteEl.focus(); } return; }
    if (note.length > NOTE_MAX) { setSaveStatus('Keep "What changed?" to 300 characters or fewer.', true); return; }
    if (editorReadOnly()) { setSaveStatus('Your code can\'t be changed right now, so it can\'t be saved.', true); return; }

    setSaveBusy(true);
    setSaveStatus('', false);
    setEditorLocked(true);
    var original = null;

    function fail(message, rollback) {
      var undo = (rollback && original)
        ? api.saveDraft(cfg.projectId, original).catch(function () { return null; })
        : Promise.resolve();
      return undo.then(function () {
        setEditorLocked(false);
        setSaveBusy(false);
        setSaveStatus(message, true);
        announce(message);
      });
    }

    Promise.resolve()
      .then(function () { return flush(); })
      .then(function () {
        original = core.getFiles();
        return api.saveDraft(cfg.projectId, editedFiles(original, name, note));
      })
      .then(function (res) {
        if (!res || res.success !== true) { throw { stage: 'draft' }; }
        return requestJson(base, 'POST', {
          projectId: cfg.projectId, linkedCourseId: cfg.linkedCourseId || '', name: name, note: note
        });
      })
      .then(function (r) {
        if (!r.ok) { return fail(errorText(r.body, 'Your version couldn\'t be saved. Try again.'), true); }
        if (r.body.preview === true) {
          return fail('Versions aren\'t stored for teachers or in preview.', true);
        }
        putMessage(messageKey(cfg), 'Saved ' + name);
        busy = false;
        window.location.reload();
      })
      .catch(function (err) {
        if (err && err.stage === 'draft') {
          return fail('Your code couldn\'t be saved. Try again.', true);
        }
        console.error(err);
        return fail('Your version couldn\'t be saved. Check your connection and try again.', true);
      });
  }

  if (saveConfirm) { saveConfirm.addEventListener('click', doSave); }
  if (saveCancel) { saveCancel.addEventListener('click', function () { closeModal(); }); }
  [nameEl].forEach(function (el) {
    if (el) { el.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); doSave(); } }); }
  });
  saveBtn.addEventListener('click', function () { openSave(saveBtn); });

  // ── Versions (list + in-dialog restore confirm) ──────────────────────────────────────────
  var rowsEl = doc.getElementById('versions-list-rows');
  var reasonEl = doc.getElementById('versions-list-reason');
  var errorEl = doc.getElementById('versions-list-error');
  var retryBtn = doc.getElementById('versions-list-retry');
  var listView = doc.getElementById('versions-list-view');
  var confirmView = doc.getElementById('versions-confirm-view');
  var confirmText = doc.getElementById('versions-confirm-text');
  var confirmGo = doc.getElementById('versions-confirm-go');
  var confirmCancel = doc.getElementById('versions-confirm-cancel');
  var listClose = doc.getElementById('versions-list-close');
  var listStatus = doc.getElementById('versions-list-status');
  var pending = null;     // { version, button }

  if (reasonEl && readOnlyReason) {
    var lockText = lock.enforce ? lockSentence(doc) : '';
    reasonEl.textContent = readOnlyReason + (lockText ? ' ' + lockText : '');
    reasonEl.hidden = false;
  }

  function setListStatus(text, isError) {
    if (!listStatus) { return; }
    listStatus.textContent = text || '';
    listStatus.classList.toggle('runner-error', !!isError);
  }

  function renderSkeleton() {
    if (!rowsEl) { return; }
    rowsEl.textContent = '';
    rowsEl.setAttribute('aria-busy', 'true');
    var li = doc.createElement('li');
    li.className = 'pcv-skel';
    li.setAttribute('aria-hidden', 'true');
    li.appendChild(doc.createElement('span'));
    li.appendChild(doc.createElement('span'));
    rowsEl.appendChild(li);
    if (errorEl) { errorEl.hidden = true; }
  }

  function renderRows() {
    if (!rowsEl) { return; }
    rowsEl.textContent = '';
    rowsEl.removeAttribute('aria-busy');
    if (errorEl) { errorEl.hidden = true; }
    if (!state.versions.length) {
      var empty = doc.createElement('li');
      empty.className = 'pcv-empty';
      empty.textContent = 'No versions yet. Use Save version to keep a copy of your code you can come back to.';
      rowsEl.appendChild(empty);
      return;
    }
    var now = Date.now();
    var canRestore = !readOnlyReason;
    state.versions.forEach(function (v) {
      var li = doc.createElement('li');
      li.className = 'pcv-row';
      var main = doc.createElement('div');
      main.className = 'pcv-row-main';
      var head = doc.createElement('div');
      head.className = 'pcv-row-head';
      var nm = doc.createElement('span');
      nm.className = 'pcv-name';
      nm.textContent = v.name || '';
      head.appendChild(nm);
      var when = relativeTime(v.createdAt, now);
      if (when) {
        var tm = doc.createElement('time');
        tm.className = 'pcv-time';
        tm.setAttribute('datetime', v.createdAt);
        tm.textContent = when;
        head.appendChild(tm);
      }
      if (v.fromMaterialId) {
        var from = doc.createElement('span');
        from.className = 'pcv-from';
        from.textContent = 'from ' + clip(v.fromMaterialName || 'an earlier activity', FROM_MAX);
        if (v.fromMaterialName && v.fromMaterialName.length > FROM_MAX) { from.title = v.fromMaterialName; }
        head.appendChild(from);
      }
      main.appendChild(head);
      var note = doc.createElement('p');
      note.className = 'pcv-note-text' + (v.auto ? ' is-auto' : '');
      note.textContent = v.auto ? 'Auto-saved' : (v.note || '');
      main.appendChild(note);
      li.appendChild(main);
      var btn = doc.createElement('button');
      btn.type = 'button';
      btn.className = 'runner-btn';
      btn.textContent = 'Restore';
      btn.setAttribute('aria-label', 'Restore ' + (v.name || 'this version'));
      if (!canRestore) {
        btn.disabled = true;
        if (reasonEl && !reasonEl.hidden) { btn.setAttribute('aria-describedby', 'versions-list-reason'); }
      }
      btn.addEventListener('click', function () { showConfirm(v, btn); });
      li.appendChild(btn);
      rowsEl.appendChild(li);
    });
  }

  function renderError() {
    if (!rowsEl) { return; }
    rowsEl.textContent = '';
    rowsEl.removeAttribute('aria-busy');
    if (errorEl) { errorEl.hidden = false; }
  }

  function showList(focusTarget) {
    pending = null;
    if (listView) { listView.hidden = false; }
    if (confirmView) { confirmView.hidden = true; }
    if (confirmGo) { confirmGo.hidden = true; }
    if (confirmCancel) { confirmCancel.hidden = true; }
    if (listClose) { listClose.hidden = false; }
    setListStatus('', false);
    if (focusTarget && focusTarget.focus) { focusTarget.focus(); }
  }

  function showConfirm(version, button) {
    if (readOnlyReason || busy) { return; }
    pending = { version: version, button: button };
    if (confirmText) {
      confirmText.textContent = 'Go back to ' + (version.name || 'this version') + '? Your current code will be saved first.';
    }
    if (listView) { listView.hidden = true; }
    if (confirmView) { confirmView.hidden = false; }
    if (confirmGo) { confirmGo.hidden = false; confirmGo.disabled = false; confirmGo.textContent = 'Go back'; }
    if (confirmCancel) { confirmCancel.hidden = false; confirmCancel.disabled = false; }
    if (listClose) { listClose.hidden = true; }
    setListStatus('', false);
    if (confirmGo) { confirmGo.focus(); }
  }

  function refreshList(focusFirst) {
    renderSkeleton();
    return loadList().then(function () {
      renderRows();
      if (focusFirst) {
        var first = rowsEl && rowsEl.querySelector('.pcv-row .runner-btn:not([disabled])');
        if (first) { first.focus(); } else if (listClose) { listClose.focus(); }
      }
    }, function () {
      renderError();
      if (focusFirst && retryBtn) { retryBtn.focus(); }
    });
  }

  function openList(from) {
    showList(null);
    showModal('versions-list-modal', from || listBtn, listClose);
    refreshList(true);
  }

  function doRestore() {
    if (busy || !pending) { return; }
    var target = pending.version;
    busy = true;
    if (confirmGo) { confirmGo.disabled = true; confirmGo.textContent = 'Restoring…'; confirmGo.setAttribute('aria-busy', 'true'); }
    if (confirmCancel) { confirmCancel.disabled = true; }
    setEditorLocked(true);

    function fail(message) {
      busy = false;
      setEditorLocked(false);
      if (confirmGo) { confirmGo.disabled = false; confirmGo.textContent = 'Go back'; confirmGo.setAttribute('aria-busy', 'false'); }
      if (confirmCancel) { confirmCancel.disabled = false; }
      setListStatus(message, true);
      announce(message);
    }

    Promise.resolve()
      .then(function () { return flush(); })
      .then(function (saved) {
        if (!saved || saved.success !== true) { return { stopped: true }; }
        return requestJson(base + '/' + encodeURIComponent(target.id) + '/restore', 'POST', {
          projectId: cfg.projectId, linkedCourseId: cfg.linkedCourseId || ''
        });
      })
      .then(function (r) {
        if (r.stopped) { return fail('Couldn\'t save your latest changes first. Try again.'); }
        if (!r.ok) { return fail(errorText(r.body, 'That version couldn\'t be restored. Try again.')); }
        if (r.body.preview === true) { return fail('Versions aren\'t restored for teachers or in preview.'); }
        putMessage(messageKey(cfg), 'Restored ' + (target.name || 'your version'));
        window.location.reload();
      })
      .catch(function (err) {
        console.error(err);
        fail('That version couldn\'t be restored. Check your connection and try again.');
      });
  }

  listBtn.addEventListener('click', function () { openList(listBtn); });
  if (retryBtn) { retryBtn.addEventListener('click', function () { refreshList(true); }); }
  if (listClose) { listClose.addEventListener('click', function () { closeModal(); }); }
  if (confirmGo) { confirmGo.addEventListener('click', doRestore); }
  if (confirmCancel) {
    confirmCancel.addEventListener('click', function () {
      if (busy) { return; }
      var back = pending && pending.button;
      showList(back);
    });
  }

  // ── the Submit nudge ─────────────────────────────────────────────────────────────────────
  var nudgeSubmit = doc.getElementById('versions-nudge-submit');
  var nudgeSave = doc.getElementById('versions-nudge-save');
  if (submitBtn && doc.getElementById('versions-nudge-modal')) {
    // Capture phase on the document, registered AFTER the test table's own Submit guard (which
    // stops a blocked click before it gets here).
    doc.addEventListener('click', function (e) {
      var t = e.target && e.target.closest ? e.target.closest('#assignment-submit') : null;
      if (!t) { return; }
      if (bypassNudge) { bypassNudge = false; return; }
      if (submitBtn.disabled || submitBtn.getAttribute('data-tests-blocked') === 'true') { return; }
      if (preview || lock.enforce || !state.loaded || state.failed) { return; }
      if (hasOwnVersion(state.versions, cfg.materialId)) { return; }
      e.preventDefault();
      e.stopImmediatePropagation();
      showModal('versions-nudge-modal', submitBtn, nudgeSave);
    }, true);
    if (nudgeSubmit) {
      nudgeSubmit.addEventListener('click', function () {
        closeModal(submitBtn);
        bypassNudge = true;
        submitBtn.click();
        bypassNudge = false;
      });
    }
    if (nudgeSave) {
      nudgeSave.addEventListener('click', function () {
        closeModal(submitBtn);
        openSave(submitBtn);
      });
    }
  }

  // ── the one-shot message from the save / restore reload ──────────────────────────────────
  var msg = takeMessage(sessionStore(), messageKey(cfg));
  if (msg) {
    announce(msg);
    try { listBtn.focus(); } catch (e) { /* focus is best-effort */ }
  }

  return { ready: firstLoad, state: state };
}
